import { UserService } from '@user/user.service';
import { ExceptionService } from './../../exceptions.service';
import { HttpErrorResponse } from '@angular/common/http';
import { IWorkbook, IErrorResponse, IUser } from '@interfaces';
import { Component, Injectable, OnInit, Inject, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { WorkbookService } from '@base/workbook/workbook.service';
import { Subscription } from 'rxjs';

import {CollectionViewer, SelectionChange} from '@angular/cdk/collections';
import {FlatTreeControl} from '@angular/cdk/tree';
import {BehaviorSubject, merge, Observable} from 'rxjs';
import {map} from 'rxjs/operators';

interface IView {
  id: string;
  name: string;
  contentUrl: string;
  createdAt: string;
  updatedAt: string;
  tags: object;
}

/** Flat node with expandable and level information */
export class DynamicFlatNode {
  constructor(public item: string, public level = 1, public expandable = false,
              public isLoading = false) {}
}

/**
 * Database for dynamic data. When expanding a node in the tree, the data source will need to fetch
 * the descendants data from the database.
 */
export class DynamicDatabase {
  dataMap = new Map<string, string[]>([
    ['Fruits', ['Apple', 'Orange', 'Banana']],
    ['Vegetables', ['Tomato', 'Potato', 'Onion']],
    ['Apple', ['Fuji', 'Macintosh']],
    ['Onion', ['Yellow', 'White', 'Purple']]
  ]);

  rootLevelNodes: string[] = ['Fruits', 'Vegetables'];

  /** Initial data from database */
  initialData(): DynamicFlatNode[] {
    return this.rootLevelNodes.map(name => new DynamicFlatNode(name, 0, true));
  }

  getChildren(node: string): string[] | undefined {
    return this.dataMap.get(node);
  }

  isExpandable(node: string): boolean {
    return this.dataMap.has(node);
  }
}

/**
 * File database, it can build a tree structured Json object from string.
 * Each node in Json object represents a file or a directory. For a file, it has filename and type.
 * For a directory, it has filename and children (a list of files or directories).
 * The input will be a json object string, and the output is a list of `FileNode` with nested
 * structure.
 */
@Injectable()
export class DynamicDataSource {

  dataChange = new BehaviorSubject<DynamicFlatNode[]>([]);

  get data(): DynamicFlatNode[] { return this.dataChange.value; }
  set data(value: DynamicFlatNode[]) {
    this.treeControl.dataNodes = value;
    this.dataChange.next(value);
  }

  constructor(private treeControl: FlatTreeControl<DynamicFlatNode>,
              private database: DynamicDatabase) {}

  connect(collectionViewer: CollectionViewer): Observable<DynamicFlatNode[]> {
    this.treeControl.expansionModel.onChange.subscribe(change => {
      if ((change as SelectionChange<DynamicFlatNode>).added ||
        (change as SelectionChange<DynamicFlatNode>).removed) {
        this.handleTreeControl(change as SelectionChange<DynamicFlatNode>);
      }
    });

    return merge(collectionViewer.viewChange, this.dataChange).pipe(map(() => this.data));
  }

  /** Handle expand/collapse behaviors */
  handleTreeControl(change: SelectionChange<DynamicFlatNode>) {
    if (change.added) {
      change.added.forEach(node => this.toggleNode(node, true));
    }
    if (change.removed) {
      change.removed.slice().reverse().forEach(node => this.toggleNode(node, false));
    }
  }

  /**
   * Toggle the node, remove from display list
   */
  toggleNode(node: DynamicFlatNode, expand: boolean) {
    const children = this.database.getChildren(node.item);
    const index = this.data.indexOf(node);
    if (!children || index < 0) { // If no children, or cannot find the node, no op
      return;
    }

    node.isLoading = true;

    setTimeout(() => {
      if (expand) {
        const nodes = children.map(name =>
          new DynamicFlatNode(name, node.level + 1, this.database.isExpandable(name)));
        this.data.splice(index + 1, 0, ...nodes);
      } else {
        let count = 0;
        for (let i = index + 1; i < this.data.length
          && this.data[i].level > node.level; i++, count++) {}
        this.data.splice(index + 1, count);
      }

      // notify the change
      this.dataChange.next(this.data);
      node.isLoading = false;
    }, 1000);
  }
}


@Component({
  //   selector: 'app-tableau-views-dialog',
  templateUrl: './tableau-filter-workbook.component.html',
  styleUrls: ['./tableau-filter-workbook.component.sass'],
  providers: [DynamicDatabase]
})

export class FilterWorkbookComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[];
  //   views: IView[];
  //   gettingViews: boolean;
  //   generatingUrl: boolean;
  //   workbook: IWorkbook;
  //   user: IUser;
  workbooks: IWorkbook[];

  treeControl: FlatTreeControl<DynamicFlatNode>;
  dataSource: DynamicDataSource;
  getLevel = (node: DynamicFlatNode) => node.level;
  isExpandable = (node: DynamicFlatNode) => node.expandable;
  hasChild = (_: number, _nodeData: DynamicFlatNode) => _nodeData.expandable;

  constructor(
    database: DynamicDatabase,
    public dialogRef: MatDialogRef<FilterWorkbookComponent>,
    @Inject(MAT_DIALOG_DATA) public data: object,
    private workbookService: WorkbookService,
    private exceptionService: ExceptionService,
    private userService: UserService,
  ) {
    this.treeControl = new FlatTreeControl<DynamicFlatNode>(this.getLevel, this.isExpandable);
    this.dataSource = new DynamicDataSource(this.treeControl, database);

    this.dataSource.data = database.initialData();

    // this.views = [];
    this.subscriptions = [];
    this.workbooks = [];
    // this.gettingViews = true;
    // this.generatingUrl = false;
    // this.workbook = data['workbook'];
    // this.user = data['user'];

    const wSubscription = this.userService.observeUserWorkbooks().subscribe(
      (w: []) => { this.workbooks = w; console.log(w); },
      (e: HttpErrorResponse) => { const err: IErrorResponse = this.exceptionService.errorHandling(e); }
    );

    // const vSubscription = this.workbookService.getViews(this.workbook).subscribe(
    //   (views: IView[]) => {
    //     this.views = views;
    //     this.gettingViews = false;
    //   },
    //   (e: HttpErrorResponse) => {const error: IErrorResponse = this.exceptionService.errorHandling(e); }
    // );

    this.subscriptions = [...this.subscriptions, wSubscription];
  }

  ngOnInit() {
    //
  }

  ngOnDestroy() {
    // this.subscriptions.map((s: Subscription) => s.unsubscribe());
  }

  selectView(view: IView) {
    // this.generatingUrl = true;
    // const arrayParams = view.contentUrl.split('/');
    // const viewName = arrayParams.pop();

    // const ticket = this.userService.getTableauParams(this.user).subscribe(
    //   t => {
    //     this.generatingUrl = false;

    //     if (parseInt(t['ticket'], 10) !== -1) {
    //       let targetUrl = `${t['host']}/trusted/${t['ticket']}/views/${this.workbook['content_url']}`;
    //       targetUrl += `/${viewName}`;
    //       this.dialogRef.close(targetUrl);
    //     } else if (t['ticket'] !== -1) {
    //       console.log('NO SE GENERO');
    //     }
    //   },
    //   (err: HttpErrorResponse) => { const error: IErrorResponse = this.exceptionService.errorHandling(err); }
    // );
  }
}
